import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/client';
import DashboardLayout from '../components/DashboardLayout';
import { DashboardSkeleton } from '../components/Skeleton';
import PulseDot from '../components/PulseDot';
import Icon from '../components/Icon';
import HealthRing from '../components/HealthRing';
import MetricCard from '../components/MetricCard';
import MiniBars from '../components/MiniBars';
import Sparkline from '../components/Sparkline';
import SeverityBadge from '../components/SeverityBadge';
import { EmptySignal } from '../components/illustrations';
import { timeAgo } from '../lib/time';
import { formatValue } from '../lib/metrics';

const CATEGORY_ORDER = ['economy', 'players', 'security', 'content', 'social'];
const CATEGORY_ICON = { economy: 'trending-down', players: 'users', security: 'shield', content: 'grid', social: 'gift' };
const DISTRIBUTION_TYPES = new Set(['players_empire_balance', 'players_level_distribution', 'economy_item_stock', 'economy_gold_supply']);

function DriverList({ drivers, t }) {
  if (!drivers || !drivers.length) return <p className="hd-clean">{t('health.all_good')}</p>;
  return (
    <ul className="health-drivers">
      {drivers.slice(0, 4).map((d, i) => (
        <li key={i}>
          <span className="hd-pts">{d.points}</span>
          <span>{t(`health.driver.${d.key}`, { ...d, defaultValue: d.key })}</span>
        </li>
      ))}
    </ul>
  );
}

function DistributionDetail({ metric, lang, t }) {
  const md = metric.metadata || {};
  if (metric.type === 'players_empire_balance') {
    return (
      <MiniBars stacked segments={[
        { label: t('dash.empire_1'), value: md.e1 || 0, color: 'var(--crit)' },
        { label: t('dash.empire_2'), value: md.e2 || 0, color: 'var(--info)' },
        { label: t('dash.empire_3'), value: md.e3 || 0, color: 'var(--ok)' },
      ]} />
    );
  }
  if (metric.type === 'players_level_distribution') {
    return (
      <MiniBars segments={[
        { label: t('dash.lvl_newbie'), value: md.newbie || 0 },
        { label: t('dash.lvl_mid'), value: md.mid || 0 },
        { label: t('dash.lvl_high'), value: md.high || 0 },
        { label: t('dash.lvl_endgame'), value: md.endgame || 0 },
      ]} />
    );
  }
  if (metric.type === 'economy_item_stock' && md.byWindow) {
    return (
      <MiniBars stacked segments={Object.entries(md.byWindow).map(([k, v]) => ({ label: k, value: v }))} />
    );
  }
  if (metric.type === 'economy_gold_supply') {
    return (
      <MiniBars stacked segments={[
        { label: t('dash.gold_player'), value: md.player || 0, color: 'var(--accent)' },
        { label: t('dash.gold_safebox'), value: md.safebox || 0, color: 'var(--info)' },
        { label: t('dash.gold_guild'), value: md.guild || 0, color: 'var(--ok)' },
      ]} />
    );
  }
  return null;
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user, activeServerKey } = useAuth();
  const lang = i18n.resolvedLanguage;

  const [loading, setLoading] = useState(true);
  const [ov, setOv] = useState(null);
  const [showSchema, setShowSchema] = useState(false);

  const load = useCallback(() => api.overview(activeServerKey || undefined).then(setOv), [activeServerKey]);
  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);
  useEffect(() => {
    const id = setInterval(() => load().catch(() => {}), 60000);
    return () => clearInterval(id);
  }, [load]);

  const name = user?.displayName || user?.email?.split('@')[0];

  if (loading) {
    return (
      <DashboardLayout title={t('dashboard.title')} heading={false}>
        <DashboardSkeleton />
      </DashboardLayout>
    );
  }

  const hasData = ov?.hasLicense && ov.categories && Object.keys(ov.categories).length > 0;
  if (!hasData) {
    return (
      <DashboardLayout title={t('dashboard.title')} heading={false}>
        <h1 className="page-title">{t('dashboard.welcome', { name })}</h1>
        <div className="empty-state">
          <EmptySignal />
          <h3>{t('dashboard.empty_title')}</h3>
          <p>{t('dashboard.empty_desc')}</p>
          <Link to="/onboarding" className="btn btn-subtle btn-sm" style={{ marginTop: 'var(--sp-5)' }}>{t('dashboard.empty_cta')}</Link>
        </div>
      </DashboardLayout>
    );
  }

  const h = ov.health || { score: 0, label: 'ok', drivers: [] };
  const agentTone = ov.agentStatus === 'active' ? 'ok' : ov.agentStatus === 'silent' ? 'crit' : 'idle';
  const agentLabel = ov.agentStatus === 'active' ? t('dashboard.status_active') : ov.agentStatus === 'silent' ? t('dashboard.status_silent') : t('dashboard.status_unknown');
  const sh = ov.schemaHealth;
  const prof = ov.serverProfile;

  return (
    <DashboardLayout title={t('dashboard.title')} heading={false}>
      <div className="dash-top">
        <div>
          <h1 className="page-title" style={{ marginBottom: 2 }}>{t('dashboard.welcome', { name })}</h1>
          <p className="page-sub" style={{ margin: 0 }}>
            {ov.serverLabel ? ov.serverLabel : t('dashboard.subtitle')}
            {prof?.maxLevel ? ` · ${t('dash.level_cap', { n: prof.maxLevel })}` : ''}
            {prof?.playerCount ? ` · ${t('dash.chars', { n: prof.playerCount.toLocaleString('tr-TR') })}` : ''}
          </p>
        </div>
        <div className="agent-pill">
          <PulseDot tone={agentTone} animated={agentTone === 'ok'} />
          {agentLabel}
          <span className="ap-time">{ov.lastHeartbeatAt ? timeAgo(ov.lastHeartbeatAt, lang) : t('dashboard.heartbeat_never')}</span>
        </div>
      </div>

      {/* Sağlık skoru */}
      <div className="card health-card">
        <HealthRing score={h.score} label={h.label} />
        <div className="health-body">
          <div className="health-title">
            {t('health.title')}
            <span className={`badge ${h.label}`}>{t(`health.label_${h.label}`)}</span>
          </div>
          <p className="health-desc">{t(`health.desc_${h.label}`)}</p>
          <DriverList drivers={h.drivers} t={t} />
        </div>
        {h.spark && h.spark.length > 2 && (
          <div className="health-spark">
            <span>{t('dash.trend_14d')}</span>
            <Sparkline data={h.spark} width={180} height={44} color={h.label === 'crit' ? 'var(--crit)' : h.label === 'warn' ? 'var(--warn)' : 'var(--ok)'} />
          </div>
        )}
      </div>

      {/* Şema sağlığı */}
      {sh && (
        <div className="schema-strip">
          <button className="ss-head" onClick={() => setShowSchema((v) => !v)}>
            <Icon name="check" size={13} style={{ color: sh.found >= sh.total - 3 ? 'var(--ok)' : 'var(--warn)' }} />
            {t('dash.schema_found', { found: sh.found, total: sh.total })}
            <Icon name="arrow" size={12} style={{ transform: showSchema ? 'rotate(90deg)' : 'none', opacity: 0.5 }} />
          </button>
          {showSchema && (
            <div className="ss-body">
              <p>{t('dash.schema_hint')}</p>
              {sh.missingList?.length > 0 && (
                <div className="ss-missing">
                  <span>{t('dash.schema_missing')}:</span>
                  {sh.missingList.map((m) => <code key={m}>{m}</code>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Kategoriler */}
      {CATEGORY_ORDER.filter((c) => ov.categories[c]).map((cat) => (
        <section key={cat} className="metric-section">
          <h2 className="ms-title"><Icon name={CATEGORY_ICON[cat]} size={15} /> {t(`dash.cat_${cat}`)}</h2>
          <div className="metric-grid">
            {ov.categories[cat].map((m) => (
              <div key={m.type} className={DISTRIBUTION_TYPES.has(m.type) ? 'mc-wrap wide' : 'mc-wrap'}>
                <MetricCard metric={m} lang={lang} />
                {DISTRIBUTION_TYPES.has(m.type) && (
                  <div className="mc-detail"><DistributionDetail metric={m} lang={lang} t={t} /></div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Son alarmlar */}
      <section className="metric-section">
        <div className="ms-title-row">
          <h2 className="ms-title"><Icon name="alert-triangle" size={15} /> {t('dashboard.recent_alerts')}</h2>
          <Link to="/dashboard/alerts" className="btn btn-subtle btn-sm">{t('dashboard.view_all')}</Link>
        </div>
        {(!ov.alerts || ov.alerts.length === 0) ? (
          <div className="card"><p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>{t('dashboard.no_alerts')}</p></div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="list">
              {ov.alerts.map((a) => (
                <div className="list-row" key={a.id} style={{ padding: 'var(--sp-4)' }}>
                  <SeverityBadge severity={a.severity} />
                  <div className="row-main">
                    <div style={{ fontWeight: 500 }}>{t(`metric.${a.type}`, a.type)}</div>
                    <div style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{a.message}</div>
                  </div>
                  <div className="row-time">{timeAgo(a.triggeredAt, lang)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
